import { type LucideIcon, MoonIcon, SunIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePreferences } from '@/hooks/usePreferences';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { THEMES } from '@/shared/constants/theme';
import { Theme } from '@/shared/types/user';
import { applyTheme, resolveTheme } from '@/util/theme';

const FORM_ID = 'profile-preferences-form';

type Form = { theme: Theme };

const THEME_OPTIONS: Record<Theme, { icon: LucideIcon; labelKey: string }> = {
  'gold-light': { icon: SunIcon, labelKey: 'profile.interface_theme.gold_light' },
  'gold-dark': { icon: MoonIcon, labelKey: 'profile.interface_theme.gold_dark' },
  light: { icon: SunIcon, labelKey: 'profile.interface_theme.light' },
  dark: { icon: MoonIcon, labelKey: 'profile.interface_theme.dark' },
  'solarized-light': { icon: SunIcon, labelKey: 'profile.interface_theme.solarized_light' },
  'solarized-dark': { icon: MoonIcon, labelKey: 'profile.interface_theme.solarized_dark' },
  'catppuccin-latte': { icon: SunIcon, labelKey: 'profile.interface_theme.catppuccin_latte' },
  'catppuccin-mocha': { icon: MoonIcon, labelKey: 'profile.interface_theme.catppuccin_mocha' },
};

export const PreferencesSection = () => {
  const { t } = useTranslation();
  const { userPreferences, updateUserPreferencesAsync } = usePreferences();

  const form = useForm<Form>({ values: { theme: resolveTheme(userPreferences?.theme) } });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = async (data: Form) => {
    await updateUserPreferencesAsync({ theme: data.theme });
    applyTheme(data.theme);
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  return (
    <SectionLayout title={t('profile.preferences')}>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={isSubmitting}
          disabled={!isDirty}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-sm leading-none font-medium text-foreground">
                {t('profile.interface_theme.title')}
              </span>
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('profile.interface_theme.subtitle')}
              </span>
            </div>
            <Controller
              control={control}
              name="theme"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full sm:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THEMES.map((theme) => {
                      const { icon: Icon, labelKey } = THEME_OPTIONS[theme];
                      return (
                        <SelectItem key={theme} value={theme}>
                          <span className="flex items-center gap-2">
                            <Icon className="size-4" />
                            {t(labelKey)}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </FieldsetCard>
      </form>
    </SectionLayout>
  );
};
